import './NotifySettings.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  BottomBar,
  Button,
  Checkbox,
  Form,
  FormItem,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { profileActions } from '$store/profile';
import { IcTelegram24 } from '$assets';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { mainActions } from '$store/main';
import { ConnectTelegramSheet, Loader } from '$shared/components';
import { notifyActions, notifySelector } from '$store/notify';
import { _confirm } from '$utils';

const tgFlag = 1 << 0;
const messageFlag = 1 << 1;
const soldMessageFlag = 1 << 2;
const commentFlag = 1 << 4;
const allowCommentFromUnfollowedFlag = 1 << 5;
const likeFlag = 1 << 6;
const allowLikeFromUnfollowedFlag = 1 << 7;
const followFlag = 1 << 8;
const giftFlag = 1 << 9;

export const NotifySettings: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const user = useCurrentUserInfo();
  const { isTelegramConnected } = useSelector(notifySelector);

  const [loadingStatus, setLoadingStatus] = useState('loading');

  const handleLoad = useCallback(() => {
    setLoadingStatus('loading');
    Api.get('/notify/settings')
      .then((resp) => {
        const data: any = resp;
        dispatch(notifyActions.setTelegramConnected(data.isTelegramConnected));
        setLoadingStatus('');
      })
      .catch(() => {
        setLoadingStatus('failed');
      });
  }, []);

  useEffect(() => {
    handleLoad();
  }, []);

  const [isEnabled, setEnabled] = useState((user.notifySettings! & tgFlag) !== tgFlag);
  const [isMessages, setMessages] = useState(
    (user.notifySettings! & messageFlag) !== messageFlag,
  );
  const [isSoldMessage, setSoldMessage] = useState(
    (user.notifySettings! & soldMessageFlag) !== soldMessageFlag,
  );

  const [isComment, setComment] = useState(
    (user.notifySettings! & commentFlag) !== commentFlag,
  );

  const [isAllowCommentFromUnfollowed, setAllowCommentFromUnfollowed] = useState(
    (user.notifySettings! & allowCommentFromUnfollowedFlag) !==
      allowCommentFromUnfollowedFlag || !isComment,
  );

  const [isLike, setLike] = useState((user.notifySettings! & likeFlag) !== likeFlag);

  const [isAllowLikeFromUnfollowed, setAllowLikeFromUnfollowed] = useState(
    (user.notifySettings! & allowLikeFromUnfollowedFlag) !==
      allowLikeFromUnfollowedFlag || !isLike,
  );

  const [isFollow, setFollow] = useState(
    (user.notifySettings! & followFlag) !== followFlag,
  );

  const [isGift, setGift] = useState(
    (user.notifySettings! & giftFlag) !== giftFlag,
  );

  const handleEnable = useCallback(() => {
    setEnabled(!isEnabled);
  }, [isEnabled]);

  const handleMessages = useCallback(() => {
    setMessages(!isMessages);
  }, [isMessages]);

  const handleSoldMessage = useCallback(() => {
    setSoldMessage(!isSoldMessage);
  }, [isSoldMessage]);

  const handleSave = useCallback(() => {
    dispatch(
      profileActions.saveNotifySettings({
        enabled: isEnabled,
        messages: isMessages,
        soldMessage: isSoldMessage,
        comment: isComment,
        allowCommentFromUnfollowed: !isAllowCommentFromUnfollowed,
        like: isLike,
        allowLikeFromUnfollowed: !isAllowLikeFromUnfollowed,
        follow: isFollow,
        gift: isGift,
      }),
    );
  }, [
    dispatch,
    isEnabled,
    isMessages,
    isSoldMessage,
    isComment,
    isAllowCommentFromUnfollowed,
    isLike,
    isAllowLikeFromUnfollowed,
    isFollow,
    isGift,
  ]);

  const handleTelegram = () => {
    dispatch(toastActions.setToastLoading());
    if (isTelegramConnected) {
      _confirm(t('notify_settings_disconnect_telegram_confirm'), () => {
        Api.post('/notify/telegram/disconnect')
          .then(() => {
            dispatch(notifyActions.setTelegramConnected(false));
            dispatch(toastActions.setToastSuccess());
          })
          .catch((err) => {
            dispatch(toastActions.setToastFail(err));
          });
      });
    } else {
      Api.post('/notify/telegram/connect')
        .then((resp) => {
          const data: any = resp;
          dispatch(toastActions.hideToast());
          dispatch(mainActions.setPopup(<ConnectTelegramSheet url={data.url} />));
        })
        .catch((err) => {
          dispatch(toastActions.setToastFail(err));
        });
    }
  };

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleLoad}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <>
        <ScrollView>
          <div className="NotifySetting__telegram_wrap">
            <List>
              <ListItem onClick={handleTelegram} icon={<IcTelegram24 />}>
                {t(
                  isTelegramConnected
                    ? 'notify_settings_disconnect_telegram'
                    : 'notify_settings_connect_telegram',
                )}
              </ListItem>
            </List>
          </div>
          {user.verified !== 2 && (
            <Form>
              <InlineHeader>{t('notify_settings_section_settings')}</InlineHeader>
              <FormItem minimize>
                <Checkbox checked={isEnabled} onClick={handleEnable}>
                  {t('notify_settings_telegram')}
                </Checkbox>
              </FormItem>
              <InlineHeader>{t('notify_settings_section_types')}</InlineHeader>
              <FormItem minimize>
                <Checkbox checked={isMessages} onClick={handleMessages}>
                  {t('notify_settings_type_message')}
                </Checkbox>
              </FormItem>
              <FormItem minimize>
                <Checkbox checked={isSoldMessage} onClick={handleSoldMessage}>
                  {t('notify_settings_section_sold_message')}
                </Checkbox>
              </FormItem>
              <FormItem minimize>
                <Checkbox checked={isComment} onClick={() => setComment(!isComment)}>
                  {t('notify_settings_type_comment')}
                </Checkbox>
                {isComment && (
                  <Checkbox
                    checked={isAllowCommentFromUnfollowed}
                    onClick={() =>
                      setAllowCommentFromUnfollowed(!isAllowCommentFromUnfollowed)
                    }
                  >
                    {t('notify_settings_type_comment_only_from_following')}
                  </Checkbox>
                )}
              </FormItem>
              <FormItem minimize>
                <Checkbox checked={isLike} onClick={() => setLike(!isLike)}>
                  {t('notify_settings_type_like')}
                </Checkbox>
                {isLike && (
                  <Checkbox
                    checked={isAllowLikeFromUnfollowed}
                    onClick={() => setAllowLikeFromUnfollowed(!isAllowLikeFromUnfollowed)}
                  >
                    {t('notify_settings_type_like_only_from_following')}
                  </Checkbox>
                )}
              </FormItem>
              <FormItem minimize>
                <Checkbox checked={isFollow} onClick={() => setFollow(!isFollow)}>
                  {t('notify_settings_type_follow')}
                </Checkbox>
              </FormItem>
              <FormItem minimize>
                <Checkbox checked={isGift} onClick={() => setGift(!isGift)}>
                  {t('notify_settings_type_gift')}
                </Checkbox>
              </FormItem>
            </Form>
          )}
        </ScrollView>
        <BottomBar>
          <Button onClick={handleSave} stretched>
            {t('save')}
          </Button>
        </BottomBar>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('notify_settings_title')}</Header>
      {renderContent()}
    </>
  );
};
